import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
import classNames from "classnames";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import animateScrollTo from "animated-scroll-to";
import { trackEvent } from "@utils/analytics";
import { BREAKPOINTS } from "@utils/breakpoints";
import { PageHeader } from "@components/pages/homepage/pageHeader";
import Layout from "@components/layout";
import { PricingPlan, PricingComparison, PricingWave, PricingAddOns } from "@components/pages/pricing";
import IconArrowRight from "@components/icons/iconArrowRight";
import { Awards, Button, CTASection, Faqs } from "@components/shared";
import { PRICING_ADD_ONS, PRICING_PLANS } from "@components/pages/pricing/pricingData";
import { SeoMetadata } from "@components/shared/seoMetadata";
import { getProcessedFaqs } from "@components/shared/faqs/faqs";
import { generateFaqSchema } from "@utils/schemaGenerators";
import * as c from "./pricing.module.scss";

gsap.registerPlugin(useGSAP, ScrollTrigger);

export default function Pricing({ data }) {
  const { footerCTA, pricingFaqs } = data;
  const [isMobile, setIsMobile] = useState(false);
  const scrollToComparison = () => {
    animateScrollTo(document.getElementById("comparison"), { speed: 600, verticalOffset: -85 });
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= BREAKPOINTS.md);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useGSAP(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    gsap.set("[data-transition='slide-enter'], [data-transition='slide-enter-child'] > *", {
      y: 50,
      opacity: 0,
      scale: 0.95,
    });

    gsap.set("[data-transition='slide-stagger-child'] > *", {
      transformOrigin: "50% 75%",
      scale: 0.5,
      y: 50,
      opacity: 0,
    });

    ScrollTrigger.batch("[data-transition='slide-enter'], [data-transition='slide-enter-child'] > *", {
      start: () => "top 105%",
      end: () => "top 100%-=25px",
      invalidateOnRefresh: true,
      onLeave: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: reduceMotion ? 0 : 0.5,
          overwrite: true,
          stagger: {
            from: "start",
            amount: reduceMotion ? 0 : 0.15,
          },
          onComplete: function () {
            gsap.set(this.targets(), { clearProps: "y", transition: "transform 0.3s ease-in-out" });
          },
        });
      },
      onLeaveBack: (elements) => {
        gsap.set(elements, {
          clearProps: "transition",
          y: 50,
          opacity: 0,
          scale: 0.95,
        });
      },
    });

    ScrollTrigger.batch("[data-transition='slide-stagger-child'] > *", {
      start: () => "top 100%+=100px",
      end: () => "bottom 100%-=10px'",
      invalidateOnRefresh: true,
      onLeave: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "saturate(1)",
          duration: reduceMotion ? 0 : 0.4,
          overwrite: true,
          stagger: {
            amount: reduceMotion ? 0 : 0.1,
          },
        });
      },
      onLeaveBack: (elements) => {
        gsap.set(elements, {
          scale: 0.5,
          y: 50,
          opacity: 0,
        });
      },
    });

    gsap.set("[data-transition='slideIn'] section *:not(div)", {
      opacity: 0,
      y: 40,
      transformOrigin: "50% 50%",
    });
    gsap.set("[data-transition='fadeIn'] > div", {
      y: 50,
      opacity: 0,
      backfaceVisibility: "hidden",
      willChange: "transform, opacity",
    });
    gsap.to("[data-transition='slideIn'] section *:not(div)", {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: reduceMotion ? 0 : 0.5,
      delay: reduceMotion ? 0 : 0.75,
      overwrite: true,
      stagger: 0.1,
    });
    gsap.to("[data-transition='fadeIn'] > div", {
      opacity: 1,
      y: 0,
      overwrite: true,
      duration: reduceMotion ? 0 : 0.5,
      delay: reduceMotion ? 0 : 1,
      stagger: {
        amount: reduceMotion ? 0 : 0.25,
      },
      onComplete: function () {
        gsap.set(this.targets(), {
          clearProps: "y",
          transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        });
      },
    });
    gsap.config({
      force3d: true,
    });
    ScrollTrigger.config({ ignoreMobileResize: true });
  }, {});

  return (
    <Layout>
      <div className={c.topBgCtn} data-transition="slideIn">
        <PageHeader className={c.header}>
          <h1 className="h1">Fair pricing—no surprises</h1>
          <p className={classNames("p2", c.headerSubline)}>
            Free data import & 1:1 onboarding assistance. Cancel anytime.
          </p>
        </PageHeader>

        <div className={c.plans}>
          <div className={c.plansInner} data-transition="fadeIn">
            {PRICING_PLANS.map((plan) => (
              <PricingPlan
                key={plan.id}
                name={plan.name}
                price={plan.price}
                mainLimit={plan.mainLimit}
                aspects={plan.aspects}
                mostPopular={plan.mostPopular}
              />
            ))}
          </div>
        </div>

        <div className={c.ctaContainer} data-transition="slide-enter">
          <h4 className={classNames("h4", c.cta)}>Ready to take the next step?</h4>
          <p className="p3">
            We'd love to give you a personalized tour and answer all your questions. If you'd rather explore for
            yourself, start a free trial to see it in action.
          </p>
          <div className={c.ctaSectionButtons}>
            <Button
              linkTo="/demo/"
              variant={isMobile ? "full" : "outline"}
              size="large"
              onClick={() => trackEvent("Pricing: Book Demo - CTA Click (Page Header)")}
            >
              Book a demo
            </Button>
            <Button
              variant={isMobile ? "outline" : "full"}
              linkTo="/trial/"
              size="large"
              onClick={() => trackEvent("Pricing: Start Free Trial - CTA Click (Page Header)")}
            >
              Start a free trial
            </Button>
          </div>
          <button className={c.comparePlans} onClick={scrollToComparison}>
            <p className={classNames("eyebrow", c.eyebrow)}>Compare Plans</p>
            <IconArrowRight version="v3" />
          </button>
        </div>
        <PricingWave />
      </div>

      <div className={c.addOns}>
        <h3 className={classNames("h3", c.addOnsTitle)} data-transition="slide-enter">
          Available add-ons
        </h3>
        <div className={c.addOnsContainer} data-transition="slide-enter-child">
          {PRICING_ADD_ONS.map((addOn) => (
            <PricingAddOns key={addOn.id} {...addOn} />
          ))}
        </div>
      </div>

      <div id="comparison" className={c.comparison}>
        <PricingComparison />
        <div className={c.comparisonCTA}>
          <Button
            linkTo="/demo/"
            variant="outline"
            onClick={() => trackEvent("Pricing: Book Demo - CTA Click (Compare Plans)")}
          >
            Book a demo
          </Button>
          <Button linkTo="/trial/" onClick={() => trackEvent("Pricing: Try it now - CTA Click (Compare Plans)")}>
            Try it now
          </Button>
        </div>
      </div>
      <div className={c.ratingsContainer}>
        <div className={c.ratingsTitleContainer}>
          <h4 className={classNames("h4", c.ratingsTitle)} data-transition="slide-enter">
            Rated #1 for customer experience
          </h4>
          <p className={classNames("p3", c.ratingsSubtitle)} data-transition="slide-enter">
            Salons and spas rave about our white-glove approach to customer onboarding and support. Making the switch
            has never been easier.
          </p>
        </div>
        <Awards />
      </div>
      <div id="faq" className={c.faq}>
        <Faqs faqs={pricingFaqs.listItems} />
      </div>
      <div className={c.bottomCTAWrapper}>
        <CTASection className={c.bottomCta} {...footerCTA} />
      </div>
    </Layout>
  );
}

export const query = graphql`
  query {
    footerCTA: contentfulLandingPageSectionFooterCta(contentfulName: { eq: "default" }) {
      ...ctaSectionFields
    }
    contentfulMetadata(id: { eq: "f92c3a80-1d4c-5c0e-baeb-f14f24e39146" }) {
      ...seoMetadataFields
    }
    pricingFaqs: contentfulGenericList(id: { eq: "e0e107a0-0ded-5c0e-a654-06680bf9267d" }) {
      heading
      listItems {
        ...listItemFaqFields
      }
    }
  }
`;

const generateSingleProductSchema = (plans) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  image: "/images/mangomint.jpg",
  name: "Mangomint Salon & Spa Software",
  description: "Modern business management software for salons and spas.",
  brand: {
    "@type": "Organization",
    name: "Mangomint",
  },
  offers: plans.map((plan) => ({
    "@type": "Offer",
    priceCurrency: "USD",
    price: String(plan.price),
    priceSpecification: {
      "@type": "PriceSpecification",
      price: String(plan.price),
      priceCurrency: "USD",
      billingDuration: "P1M",
      valueAddedTaxIncluded: false,
    },
    url: "https://www.mangomint.com/pricing",
    category: plan.name,
  })),
});

export function Head({ data }) {
  const contentfulMetadata = data?.contentfulMetadata;
  const productSchema = generateSingleProductSchema(PRICING_PLANS);
  const faqs = getProcessedFaqs(data?.pricingFaqs?.listItems || []);

  return (
    <>
      <SeoMetadata {...contentfulMetadata} />
      <body className={c.body} />
      {faqs.length > 0 && (
        <script
          type="application/ld+json"
          id="faq-schema-pricing"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFaqSchema(faqs)) }}
        />
      )}
      <script
        type="application/ld+json"
        id="product-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
    </>
  );
}
